import GiftSenderDrawer from "@modules/gift/GiftSenderDrawer";
import GiftFinderDrawer from "@modules/giftFinder/GiftFinderDrawer";
import { useAppContext } from "@context/app";
import useGiftFinderDrawer from "@hooks/useGiftFinderDrawer";
import { useUI } from "@context/ui-managed-context";
import { IGiftFinderPayloadQuestion } from "@types";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import RamadanRiddlesDrawer from "@modules/ramadan/RamadanRiddlesDrawer";

const GiftListDrawer = dynamic(
    () => import("@modules/gift/GiftRecipientDrawer").catch(() => () => null),
    { ssr: false }
);

const HomeDrawers: React.FC = () => {
    const router = useRouter();
    const { user, syncData } = useAppContext();
    const { setModalView, openModal } = useUI();

    const [isGiftDrawerOpen, setIsGiftDrawerOpen] = useState(!!router.query.send_gift);
    const [isGiftListDrawerOpen, setIsGiftListDrawerOpen] = useState(false);
    const {
        isOpen: isGiftFinderOpen,
        openDrawer: openGiftFinderDrawer,
        closeDrawer: closeGiftFinderDrawer,
    } = useGiftFinderDrawer();
    const [isRamadanRiddlesDrawerOpen, setIsRamadanRiddlesDrawerOpen] = useState(false);

    useEffect(() => {
        setIsGiftDrawerOpen(!!router.query.send_gift);
    }, [router.query.send_gift]);

    useEffect(() => {
        const removeHashFragment = () => {
            if (typeof window !== "undefined") {
                const newUrl = window.location.pathname + window.location.search;
                window.history.replaceState({}, document.title, newUrl);
            }
        };

        const handleAppLinks = () => {
            const currentPath = window.location.href;

            if (currentPath.includes("#openSendDigitalGift")) {
                setIsGiftDrawerOpen(true);
                removeHashFragment();
            }

            if (currentPath.includes("#openListDigitalGift")) {
                setIsGiftListDrawerOpen(true);
                removeHashFragment();
            }

            if (currentPath.includes("#openGiftFinder")) {
                openGiftFinderDrawer();
                removeHashFragment();
            }

            if (currentPath.includes("#openRamadanRiddles")) {
                if (syncData?.user) {
                    setIsRamadanRiddlesDrawerOpen(true);
                } else {
                    setIsRamadanRiddlesDrawerOpen(false);
                    setModalView("LOGIN_VIEW", true, () => { });
                    openModal();
                }
                removeHashFragment();
            }

            if (router.query.send_gift) {
                setIsGiftDrawerOpen(true);
            }
        };

        handleAppLinks();
        router.events.on("routeChangeComplete", handleAppLinks);

        if (typeof window !== "undefined") {
            window.addEventListener("hashchange", handleAppLinks);
        }

        return () => {
            router.events.off("routeChangeComplete", handleAppLinks);

            if (typeof window !== "undefined") {
                window.removeEventListener("hashchange", handleAppLinks);
            }
        };
    }, [openGiftFinderDrawer, openModal, router, setModalView, syncData?.user]);

    const handleGiftFinderSubmit = useCallback(
        async (payloadQuestions: IGiftFinderPayloadQuestion[]) => {
            await router.push({
                pathname: "/product",
                query: {
                    gift_finder_questions: JSON.stringify(payloadQuestions),
                },
            });
        },
        [router]
    );

    return (
        <>
            <GiftSenderDrawer
                isOpen={isGiftDrawerOpen}
                onClose={() => setIsGiftDrawerOpen(false)}
            />
            {GiftListDrawer && (
                <GiftListDrawer
                    isOpen={isGiftListDrawerOpen}
                    onClose={() => setIsGiftListDrawerOpen(false)}
                    digital_gifts={user.data?.digital_gifts || []}
                />
            )}
            <GiftFinderDrawer
                isOpen={isGiftFinderOpen}
                onClose={closeGiftFinderDrawer}
                onSubmit={handleGiftFinderSubmit}
            />
            <RamadanRiddlesDrawer
                isOpen={isRamadanRiddlesDrawerOpen}
                onClose={() => setIsRamadanRiddlesDrawerOpen(false)}
            />
        </>
    );
};

export default HomeDrawers;
